/**
 * Pure helpers that convert a /api/v1/videos response into the shape the
 * JobDashboard stat cards expect.
 *
 * Extracted from JobDashboard so the logic can be unit-tested without
 * rendering the full component (which depends on Ant Design, React Query,
 * sockets, routing, etc.).
 *
 * Server contract (web-api GET /api/v1/videos, post Story 5.x):
 *   {
 *     jobs: [...],
 *     total: <number>,
 *     status_counts: { processing?, uploading?, completed?, error?, failed? },
 *     returned, limit, offset, has_more
 *   }
 *
 * Both fallbacks below are intentional:
 *   - Older web-api revisions (pre status_counts) should still produce
 *     sensible numbers, computed from the current page.
 *   - Status name drift ("error" vs "failed") is tolerated by summing
 *     whichever keys exist.
 */

const DEFAULT_STATS = Object.freeze({
  total: 0,
  processing: 0,
  completed: 0,
  failed: 0,
});

/**
 * Compute the dashboard summary stats.
 *
 * @param {Array<Object>} jobsList - the jobs on the current page.
 * @param {Object} [serverResponse] - raw response from videoApi.getAllJobs.
 * @returns {{ total: number, processing: number, completed: number, failed: number }}
 */
export function computeDashboardStats(jobsList = [], serverResponse) {
  const serverTotal = serverResponse && typeof serverResponse.total === 'number'
    ? serverResponse.total
    : null;
  const serverCounts = serverResponse && serverResponse.status_counts
    ? serverResponse.status_counts
    : null;

  if (serverTotal !== null && serverCounts) {
    return {
      total: serverTotal,
      processing: (serverCounts.processing || 0) + (serverCounts.uploading || 0),
      completed: serverCounts.completed || 0,
      // Server historically uses "error"; accept either spelling so a
      // future rename on the backend doesn't silently zero the card.
      failed: (serverCounts.error || 0) + (serverCounts.failed || 0),
    };
  }

  // Fallback for older servers without server-side totals: derive from the
  // current page. This matches pre-patch behavior so the dashboard keeps
  // working even against a stale deployment.
  const fallbackTotal = serverTotal !== null ? serverTotal : jobsList.length;
  return {
    total: fallbackTotal,
    processing: jobsList.filter((j) => j.status === 'processing' || j.status === 'uploading').length,
    completed: jobsList.filter((j) => j.status === 'completed').length,
    failed: jobsList.filter((j) => j.status === 'error' || j.status === 'failed').length,
  };
}

export { DEFAULT_STATS };
